/**
 * ObstacleModel: renders Synth Riders obstacle FBX models in the 3D tunnel.
 * Per-obstacle config: shape, scaleX/Y, defaultX/Y, rotation.
 * Global color settings: fillColor, fillOpacity, fresnelColor, fresnelOpacity, fresnelIntensity.
 *
 * PERFORMANCE MODEL (matches the note renderer's Stage-A overhaul):
 *  - The fresnel rim is FOLDED into the fill material via onBeforeCompile (one
 *    mesh per FBX geometry instead of two, halving transparent draws).
 *  - Materials are SHARED: exactly two per settings snapshot (normal + dimmed),
 *    cached at module level — obstacles never create materials, they swap
 *    between the two when crossing the trigger.
 *  - There is NO per-obstacle useFrame. Every mounted obstacle registers into a
 *    module registry and ONE `tickObstacles()` call (from the windowed renderer)
 *    updates z-position, grid-pulse and dim state for all of them.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useFBX, useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useEditorStore } from '../../store/editorStore';
import type { ObstacleConfigKey } from '../../store/editorStore';
import { OBSTACLE_SHAPE_FBX, DEFAULT_OBSTACLE_CONFIGS } from '../../store/editorStore';
import type { ObstacleShape } from '../../store/editorSettings';
import type { Slide, Obstacle } from '../../types/beatmap';
import { WALL_COLORS, parseWallColor, GRID_SCALE } from '../../types/beatmap';
import { BEAT_SPACING, chartBeat, VISIBLE_BEATS_BEHIND } from './TunnelScene';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PERF, RENDER_STATS } from '../../perf/adaptiveBudget';

// ── Constants (must match TunnelScene.tsx) ────────────────────────────────────

const NOTE_Y_OFFSET = 1.8;
const TUNNEL_WIDTH = 5;

// ── Preload all FBX/texture paths at module scope ────────────────────────────

const allShapeDefs = Object.values(OBSTACLE_SHAPE_FBX);
const uniqueFbxPaths = new Set(allShapeDefs.map(d => d.fbx));
const uniqueTexPaths = new Set(allShapeDefs.map(d => d.texture));

uniqueFbxPaths.forEach(path => useFBX.preload(path));
uniqueTexPaths.forEach(path => useTexture.preload(path));

// ── Shared obstacle materials (fill + folded-in fresnel rim) ─────────────────
// Two materials per settings snapshot: normal and dimmed (behind the trigger).
// Cached by a settings key so ALL obstacles share the same two instances and
// dimming is a material SWAP, not a per-frame traverse + mutate.

interface ObstacleMats { normal: THREE.MeshStandardMaterial; dim: THREE.MeshStandardMaterial }

function buildObstacleMaterial(opts: {
  fillColor: string; fillOpacity: number;
  fresColor: string; fresOpacity: number; fresIntensity: number;
  dim: number;
  // When an outline atlas is supplied it becomes the emissiveMap and the
  // emissive channel is repurposed for it (outlineColor × outlineEmission), so
  // only the face borders glow. Otherwise the emissive is a faint fill self-glow.
  outlineMap?: THREE.Texture | null;
  outlineColor?: string; outlineEmission?: number;
  // Wireframe style: drive the fragment ALPHA from the outline/border atlas so
  // only the edges are opaque (body see-through) — matches the game's
  // "Wireframe Obstacle" material (border texture only, no solid fill).
  wireframe?: boolean;
}): THREE.MeshStandardMaterial {
  const hasOutline = !!opts.outlineMap;
  const m = new THREE.MeshStandardMaterial({
    color: new THREE.Color(opts.fillColor),
    emissive: new THREE.Color(hasOutline ? (opts.outlineColor || '#ffffff') : opts.fillColor),
    emissiveIntensity: (hasOutline ? (opts.outlineEmission ?? 1.5) : 0.15) * opts.dim,
    transparent: true,
    opacity: opts.fillOpacity * opts.dim,
    metalness: 0.1,
    roughness: 0.8,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  if (opts.outlineMap) m.emissiveMap = opts.outlineMap;
  const fresCol = new THREE.Color(opts.fresColor);
  const fresOpacity = opts.fresOpacity * opts.dim;
  const fresIntensity = opts.fresIntensity;
  const wire = !!opts.wireframe;
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uFresCol = { value: fresCol };
    shader.uniforms.uFresOpacity = { value: fresOpacity };
    shader.uniforms.uFresIntensity = { value: fresIntensity };
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {',
        'uniform vec3 uFresCol;\nuniform float uFresOpacity;\nuniform float uFresIntensity;\nvoid main() {')
      .replace('#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        float obsFres = pow(1.0 - saturate(abs(dot(normalize(vViewPosition), normal))), 2.0);
        totalEmissiveRadiance += uFresCol * obsFres * uFresIntensity * uFresOpacity;
        diffuseColor.a = clamp(diffuseColor.a + obsFres * uFresOpacity, 0.0, 1.0);
        ${wire ? `
        // Wireframe: the border atlas (white edges on black) IS the alpha mask,
        // so the solid body drops out and only the glowing edges remain.
        #ifdef USE_EMISSIVEMAP
          float wireA = texture2D( emissiveMap, vEmissiveMapUv ).r;
          diffuseColor.a = clamp(wireA * 1.4, 0.0, 1.0);
        #endif` : ''}`);
  };
  m.customProgramCacheKey = () => (wire ? 'obstacle-wireframe' : 'obstacle-fill-fresnel');
  return m;
}

/** Build a standalone obstacle-STYLE material tinted by `color` (used by the
 *  decor-wall layer so decorative walls look exactly like real obstacles — same
 *  folded-in fresnel rim + transparency, just a per-group fill tint). When
 *  `selected` it glows brighter so the picked group stands out.
 *
 *  `outlineMap` is the game's per-shape outline atlas (white edges on black,
 *  e.g. Mat_Color_RegularObstacle_Outlines.png). Fed as the emissiveMap so ONLY
 *  the face borders emit — and because emissive = the group colour, the outline
 *  glows in the chosen colour (option A). The base shader's emissivemap_fragment
 *  is preserved by buildObstacleMaterial's onBeforeCompile, so the map samples
 *  before the fresnel rim is added on top. Caller owns disposal. */
export function createDesignMaterial(color: string, gp: {
  obstacleFillOpacity: number;
  obstacleFresnelColor: string; obstacleFresnelOpacity: number; obstacleFresnelIntensity: number;
  obstacleOutlineEmission?: number;
}, selected: boolean, outlineMap?: THREE.Texture | null, wireframe = false): THREE.MeshStandardMaterial {
  const emission = gp.obstacleOutlineEmission ?? 1.5;
  if (wireframe) {
    // Edges-only style (matches the game's "Wireframe Obstacle"): the border
    // atlas drives alpha so the body is see-through and only the edges glow.
    const wm = buildObstacleMaterial({
      fillColor: color, fillOpacity: 1,
      fresColor: gp.obstacleFresnelColor, fresOpacity: 0, fresIntensity: 0, dim: 1,
      outlineMap: outlineMap ?? null, outlineColor: color,
      outlineEmission: emission * 1.6 * (selected ? 1.5 : 1),
      wireframe: true,
    });
    // See-through: don't occlude walls behind (no solid body to write depth for).
    wm.depthWrite = false;
    // No outline atlas for this shape → fall back to mesh-edge wireframe so the
    // wall is still visible rather than fully transparent.
    if (!outlineMap) {
      wm.wireframe = true;
      wm.opacity = 1;
      wm.emissive = new THREE.Color(color);
      wm.emissiveIntensity = 1.2 * (selected ? 1.5 : 1);
    }
    return wm;
  }
  // Design is DECORATION — meant to be seen at all times, not a subtle gameplay
  // hazard. So it renders with a clearly-visible fill FLOOR (well above the
  // faint real-obstacle fill), whether or not it's selected — otherwise an
  // unselected wall art reads as "not there" until you click it. Selection then
  // adds an EXTRA highlight (brighter fill + outline glow) on top, rather than
  // being the difference between visible and invisible.
  const baseOpacity = Math.min(1, gp.obstacleFillOpacity + 0.3);
  // Build WITH the outline at construction (same robust path as real obstacles),
  // but the outline colour is the per-group `color` (panel-controlled) rather
  // than the global obstacle outline colour.
  const m = buildObstacleMaterial({
    fillColor: color, fillOpacity: baseOpacity,
    fresColor: gp.obstacleFresnelColor, fresOpacity: gp.obstacleFresnelOpacity,
    fresIntensity: gp.obstacleFresnelIntensity, dim: 1,
    outlineMap: outlineMap ?? null, outlineColor: color, outlineEmission: emission,
  });
  if (selected) {
    m.emissiveIntensity = (outlineMap ? emission : 0.7) * 1.5;
    m.opacity = Math.min(1, baseOpacity + 0.25);
  }
  // Design is built from MANY overlapping solid shapes (a composite piece), so it
  // must occlude itself correctly — write depth. buildObstacleMaterial defaults
  // to depthWrite:false (right for the game's faint see-through hazards, wrong
  // here: without it the overlapping decor walls "mount" on top of each other
  // instead of nearer ones hiding farther ones). The fill is opaque enough
  // (≥0.45) that writing depth reads as solid; the fresnel rim + outline still
  // glow on top. depthWrite is safe with transparent:true — the depth TEST then
  // resolves occlusion regardless of the instances' draw order.
  m.depthWrite = true;
  return m;
}

// Materials are cached PER SHAPE now (each shape has its own outline atlas fed
// as the emissiveMap), keyed by shape. When any global obstacle setting changes
// the whole cache is disposed + rebuilt.
interface ObstacleGP {
  obstacleFillColor: string; obstacleFillOpacity: number;
  obstacleFresnelColor: string; obstacleFresnelOpacity: number; obstacleFresnelIntensity: number;
  obstacleOutlineColor: string; obstacleOutlineEmission: number;
}
let _obsSettingsKey = '';
const _obsMatsCache = new Map<string, ObstacleMats>();

function getObstacleMaterials(gp: ObstacleGP, shape: ObstacleShape, outlineMap: THREE.Texture | null): ObstacleMats {
  const settingsKey = `${gp.obstacleFillColor}|${gp.obstacleFillOpacity}|${gp.obstacleFresnelColor}|${gp.obstacleFresnelOpacity}|${gp.obstacleFresnelIntensity}|${gp.obstacleOutlineColor}|${gp.obstacleOutlineEmission}`;
  if (settingsKey !== _obsSettingsKey) {
    for (const m of _obsMatsCache.values()) { m.normal.dispose(); m.dim.dispose(); }
    _obsMatsCache.clear();
    _obsSettingsKey = settingsKey;
  }
  const cached = _obsMatsCache.get(shape);
  if (cached) return cached;
  const base = {
    fillColor: gp.obstacleFillColor, fillOpacity: gp.obstacleFillOpacity,
    fresColor: gp.obstacleFresnelColor, fresOpacity: gp.obstacleFresnelOpacity,
    fresIntensity: gp.obstacleFresnelIntensity,
    outlineMap, outlineColor: gp.obstacleOutlineColor, outlineEmission: gp.obstacleOutlineEmission,
  };
  const mats: ObstacleMats = {
    normal: buildObstacleMaterial({ ...base, dim: 1 }),
    dim: buildObstacleMaterial({ ...base, dim: 0.2 }),
  };
  _obsMatsCache.set(shape, mats);
  return mats;
}

// ── Central obstacle ticker ──────────────────────────────────────────────────
// Every mounted ObstacleModel registers here; the windowed renderer calls
// tickObstacles() once per frame — replacing N useFrame callbacks each doing a
// full traverse + material mutation with one flat loop + material swaps.

interface ObstacleEntry {
  beats: number;
  group: THREE.Group;
  meshes: THREE.Mesh[];
  matsRef: { current: ObstacleMats | null };  // this obstacle's shape material pair
  appliedMats: ObstacleMats | null;           // last pair the ticker pushed onto the meshes
  pulseStart: number;
  prevOnGrid: boolean;
  prevRel?: number;   // last frame's (beats - rawBeat) — to catch the trigger crossing
  dimmed: boolean;
}

const _obstacleReg = new Set<ObstacleEntry>();

export function tickObstacles(): void {
  if (_obstacleReg.size === 0) return;
  const store = useEditorStore.getState();
  const rawBeat = chartBeat();   // offset-adjusted chart beat (NOT the raw time beat)
  const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const gridTol = Math.max(0.006, store.scrollStep * 0.55);
  const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
  let vis = 0;

  for (const e of _obstacleReg) {
    e.group.position.z = -e.beats * BEAT_SPACING;

    // Draw-distance (FOV) cull: hide obstacles outside the adaptive horizon —
    // ahead past the FOV, and more than `behind` beats back (tied to the FOV so a
    // tight FOV also drops passed walls). The window pre-mounts within a margin so
    // a wall never hitches as it crosses into view.
    const rel = e.beats - rawBeat;
    e.group.visible = rel <= PERF.obstacleViewBeats && rel >= -behind;
    if (!e.group.visible) continue;
    vis++;

    // Grid-alignment pop: only a scrubbing/editing aid (confirms an obstacle is
    // exactly on the beat while you drag the playhead by hand). During actual
    // playback it stays off — the obstacle just passes through the trigger line
    // like real gameplay, no pop. Trigger state (onGrid/crossed) is still tracked
    // every frame regardless of play state, so the detector is already correct
    // the instant the user pauses/scrubs (no stale prevRel from a frame skip).
    const onGrid = Math.abs(rel) < gridTol;
    const crossed = e.prevRel !== undefined && e.prevRel > 0 && rel <= 0;
    const trig = onGrid || crossed;
    if (trig && !e.prevOnGrid) e.pulseStart = nowMs;
    e.prevOnGrid = trig;
    e.prevRel = rel;
    let pulse = 1;
    if (!store.playback.playing && e.pulseStart) {
      const dt = (nowMs - e.pulseStart) / 1000;
      const PD = 0.18;
      if (dt < PD) pulse = 1 + 0.4 * Math.sin(Math.PI * (dt / PD));
      else e.pulseStart = 0;
    }
    e.group.scale.setScalar(pulse);

    // Behind the trigger + off the snap → swap to this obstacle's dimmed
    // material. Also re-apply when the material pair itself changed (settings
    // edit rebuilt the per-shape materials) so the swap state stays correct.
    const pair = e.matsRef.current;
    const dim = e.beats < rawBeat && !onGrid;
    if (pair && (pair !== e.appliedMats || dim !== e.dimmed)) {
      e.dimmed = dim;
      e.appliedMats = pair;
      const mat = dim ? pair.dim : pair.normal;
      for (const mesh of e.meshes) mesh.material = mat;
    }
  }
  // Stats for the classic (selected/dragging) obstacles the ticker still owns;
  // the instanced pools add their far-larger counts on top (see below). `vis` is
  // exposed so the pool writer can include the classic visibles in obsVisible.
  _classicObsVisible = vis;
}

// Visible count from the last tickObstacles() pass (classic selected obstacles),
// read by the instanced-pool writer so the HUD's obsVisible covers both paths.
let _classicObsVisible = 0;

// ── Extract all BufferGeometry from an FBX group (cached once per fbx path) ──

function extractGeometries(fbx: THREE.Group): THREE.BufferGeometry[] {
  const geoms: THREE.BufferGeometry[] = [];
  fbx.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      geoms.push((child as THREE.Mesh).geometry);
    }
  });
  return geoms;
}

// ── Per-obstacle config keys that we support ─────────────────────────────────

export const CONFIGURABLE_KEYS = new Set<string>([
  'wall_left', 'wall_right', 'angle_left', 'angle_right', 'center', 'crouch',
]);

// ── Legacy-editor position sanitizing ────────────────────────────────────────
// wall_left/wall_right/angle_left/angle_right are FIXED-LANE obstacles — this
// app's own gizmo drag (TunnelScene onCtrlDragMove) is the only legitimate way
// to move one, and it writes X in the same small, GRID_SCALE-normalized space
// as the wall type's configured defaultX (roughly ±0.45 to ±2, bounded by the
// tunnel width). Some .synth files exported by the legacy Synth Riders VR
// editor carry a leftover `position` field on these lane-locked slides that
// has nothing to do with the lane — observed values range from near-zero
// noise (e.g. 0.002, identical for BOTH wall_left and wall_right in the same
// file — a dead giveaway it isn't a real per-side offset) to raw external
// coordinates on a completely different scale (e.g. ±10.25). Trusting it
// verbatim collapses the wall toward the tunnel centre (small values) or off
// past the visible track (huge values). Only accept a per-instance X when its
// sign matches the wall's side AND its magnitude is plausible for this app's
// coordinate space; otherwise fall back to the wall type's configured lane.
// Junk detection threshold: legacy noise collapses a wall to the ORIGIN, e.g. the
// [0.002, 0.0012, z] seen identically on every wall_left AND wall_right in the
// same file — a dead giveaway it is not a real per-instance offset. Real authored
// positions are never that close to zero on both axes at once.
const LANE_X_MIN = 0.15;

export function resolveWallXY(
  _wallType: string,
  position: number[] | null | undefined,
  cfg: { defaultX: number; defaultY: number } | null,
): [number, number] {
  const fallbackX = cfg ? cfg.defaultX : 0;
  const fallbackY = cfg ? cfg.defaultY : 0;
  if (!position) return [fallbackX, fallbackY];
  const ax = Math.abs(position[0] ?? 0);
  const ay = Math.abs(position[1] ?? 0);
  // Only fall back to the wall type's configured lane when the position is
  // ACTUALLY junk — collapsed to the origin on BOTH axes. Anything else is a real
  // authored position and is used verbatim, however far out it sits.
  //
  // There is deliberately NO upper magnitude clamp: decorative wall art is
  // authored across a wide area (a real map measured here spans |x| up to 5.5 and
  // y from -4.4 to +5.6, with rotations in 60° steps — a radial pattern). The old
  // `|x| > 3` guard threw away 46% of that file's walls and snapped them back to
  // the default lane, which is exactly the "walls load in wrong positions, mostly
  // wall art placed far from the grid" report. A wall's type selects its MESH; it
  // does not constrain where the author put it.
  if (ax < LANE_X_MIN && ay < LANE_X_MIN) {
    return [fallbackX, fallbackY];
  }
  return [position[0], position[1]];
}

// ── Single Obstacle FBX Renderer ─────────────────────────────────────────────

function ObstacleFBX({ wallType, meshesOut, matsRef }: { wallType: string; meshesOut: THREE.Mesh[]; matsRef: { current: ObstacleMats | null } }) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const obstacleScale = gp.obstacleScale;
  const configs = gp.obstacleConfigs;

  // Per-obstacle config (shape, scale, position, rotation)
  const cfg = CONFIGURABLE_KEYS.has(wallType)
    ? configs[wallType as ObstacleConfigKey]
    : DEFAULT_OBSTACLE_CONFIGS[wallType as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS.center;

  // Resolve shape → FBX/texture paths
  const shapeDef = OBSTACLE_SHAPE_FBX[cfg.shape];
  const fbx = useFBX(shapeDef.fbx);
  // The shape's outline atlas → emissiveMap (so only the face borders glow).
  const outline = useTexture(shapeDef.texture);
  useMemo(() => { if (outline) { outline.colorSpace = THREE.SRGBColorSpace; outline.needsUpdate = true; } }, [outline]);

  // Extract geometries once from the loaded FBX (stable reference)
  const geometries = useMemo(() => extractGeometries(fbx), [fbx]);

  // Per-shape materials (normal + dimmed), rebuilt when a global obstacle
  // setting changes. Exposed to the parent's ticker entry via matsRef.
  const mats = useMemo(() => getObstacleMaterials(gp, cfg.shape, outline), [
    gp.obstacleFillColor, gp.obstacleFillOpacity,
    gp.obstacleFresnelColor, gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity,
    gp.obstacleOutlineColor, gp.obstacleOutlineEmission, cfg.shape, outline,
  ]); // eslint-disable-line react-hooks/exhaustive-deps
  matsRef.current = mats;

  // Mirror left variants (wall_left, angle_left have negative scaleX historically)
  const isLeft = wallType === 'wall_left' || wallType === 'angle_left';
  const baseX = isLeft ? -obstacleScale : obstacleScale;
  const sx = baseX * (cfg.scaleX ?? 1);
  const sy = obstacleScale * (cfg.scaleY ?? 1);
  const sz = obstacleScale * (cfg.scaleZ ?? 1);

  const rotZ = (cfg.rotation * Math.PI) / 180; // degrees → radians

  // Collect mesh refs for the ticker's material swap.
  const collect = (mesh: THREE.Mesh | null) => {
    if (mesh && !meshesOut.includes(mesh)) meshesOut.push(mesh);
  };

  return (
    <group scale={[sx, sy, sz]} rotation={[0, 0, rotZ]}>
      {geometries.map((geom, i) => (
        <mesh key={i} ref={collect} geometry={geom} material={mats.normal} />
      ))}
    </group>
  );
}

// ── Fallback Line Marker (for unknown wall types) ────────────────────────────

function FallbackSlideMarker({ color }: { color: string }) {
  const { color: rgb, alpha } = parseWallColor(color);

  return (
    <>
      <mesh position={[0, -0.5, 0]}>
        <boxGeometry args={[TUNNEL_WIDTH, 0.04, 0.04]} />
        <meshStandardMaterial
          color={rgb}
          emissive={rgb}
          emissiveIntensity={0.4}
          transparent
          opacity={alpha * 0.6}
        />
      </mesh>
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[TUNNEL_WIDTH, 0.04, 0.04]} />
        <meshStandardMaterial
          color={rgb}
          emissive={rgb}
          emissiveIntensity={0.4}
          transparent
          opacity={alpha * 0.6}
        />
      </mesh>
    </>
  );
}

// ── Instanced obstacle pools ─────────────────────────────────────────────────
// Every NON-selected wall obstacle renders through per-shape InstancedMesh pools
// (a normal + a dimmed pool per shape) instead of an individual group-of-meshes
// each. Result: a constant handful of draw calls for the whole dense section
// (≤ 5 shapes × 2 pools) rather than N × (sub-mesh count). Mirrors the note
// InstancedNotePools: ONE writer composes every visible instance's matrix each
// frame, classifies normal vs dim by beat, compacts into pool slots, and sets
// mesh.count. Selected / dragging obstacles are EXCLUDED (WindowedSlides renders
// those as the classic ObstacleModel with its highlight + gizmo) — always few.
//
// The pools live inside <BeatScrollGroup> (like the notes), so each instance
// sits at a static z = -beat·BEAT_SPACING and the parent group's per-frame
// translation scrolls them — no per-frame z bookkeeping here.

const DEG2RAD = Math.PI / 180;
const OBS_POOL_LIMIT = 320;            // ≥ PERF.maxObstacles ceiling (300)
// Every shape a wall_type can resolve to (the 6 configurable types map onto
// regular / diagonal / center; square / triangle are here in case a wall is
// re-shaped in settings). Fixed length → stable hook order.
const WALL_SHAPES: ObstacleShape[] = ['regular', 'diagonal', 'center', 'square', 'triangle'];
type ObsPool = 'normal' | 'dim';
const OBS_POOLS: ObsPool[] = ['normal', 'dim'];

// Merged geometry per shape — the whole shape as a single buffer so it's one
// instanced draw. CRITICAL: we merge EXACTLY the geometries the classic
// ObstacleFBX renders (extractGeometries → raw child geometry, in local space).
// We must NOT bake the FBX node/root world matrix (as mergeFbxGeometry does):
// obstacleScale + the per-type scaleX/Y/Z are calibrated against this raw
// geometry, so baking the root transform and THEN applying that scale would
// double-transform the wall — it renders stretched / mis-rotated. Merging the
// raw geometries makes the instanced wall byte-identical to the classic one.
// `rotX` bakes a base X-rotation into the merged buffer — 0 for wall slides
// (already upright), Math.PI/2 for the Square/Triangle AIR obstacles whose FBX
// are authored lying flat (face in the XZ plane) and must stand up to face the
// player. Cached per (shape, rotX).
const _obsMergedGeom = new Map<string, THREE.BufferGeometry>();
export function getObstacleMergedGeometry(shape: ObstacleShape, fbx: THREE.Group, rotX = 0): THREE.BufferGeometry | null {
  const key = `${shape}:${rotX}`;
  const cached = _obsMergedGeom.get(key);
  if (cached) return cached;
  const raw = extractGeometries(fbx);
  if (!raw.length) return null;
  // Normalise each sub-geometry to a consistent {position, normal, uv} non-indexed
  // buffer so mergeGeometries can concatenate them (raw local space — no matrix).
  const parts = raw.map((g) => {
    const src = g.index ? g.toNonIndexed() : g.clone();
    const pos = src.getAttribute('position') as THREE.BufferAttribute;
    const out = new THREE.BufferGeometry();
    out.setAttribute('position', pos.clone());
    let nrm = src.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!nrm) { src.computeVertexNormals(); nrm = src.getAttribute('normal') as THREE.BufferAttribute; }
    out.setAttribute('normal', nrm.clone());
    const uv = src.getAttribute('uv') as THREE.BufferAttribute | undefined;
    out.setAttribute('uv', uv ? uv.clone() : new THREE.BufferAttribute(new Float32Array(pos.count * 2), 2));
    return out;
  });
  const merged = parts.length === 1 ? parts[0] : (mergeGeometries(parts, false) ?? parts[0]);
  if (rotX) merged.rotateX(rotX);
  merged.computeBoundingSphere();
  _obsMergedGeom.set(key, merged);
  return merged;
}

// Reused scratch (no per-instance allocation in the writer).
const _oPos = new THREE.Vector3();
const _oQuat = new THREE.Quaternion();
const _oEuler = new THREE.Euler();
const _oScale = new THREE.Vector3();
const _oMatrix = new THREE.Matrix4();

interface ObsPopState { pulseStart: number; prevOnGrid: boolean; prevRel: number }

export function InstancedObstaclePools({ entries }: { entries: Array<{ slide: Slide; index: number }> }) {
  const gp = useEditorStore(s => s.gameplaySettings);

  // Load every wall shape (FBX + outline atlas). Preloaded at module scope, so
  // this is cheap and unconditional (stable hook order — never in a loop).
  const fbxRegular  = useFBX(OBSTACLE_SHAPE_FBX.regular.fbx);
  const fbxDiagonal = useFBX(OBSTACLE_SHAPE_FBX.diagonal.fbx);
  const fbxCenter   = useFBX(OBSTACLE_SHAPE_FBX.center.fbx);
  const fbxSquare   = useFBX(OBSTACLE_SHAPE_FBX.square.fbx);
  const fbxTriangle = useFBX(OBSTACLE_SHAPE_FBX.triangle.fbx);
  const texRegular  = useTexture(OBSTACLE_SHAPE_FBX.regular.texture);
  const texDiagonal = useTexture(OBSTACLE_SHAPE_FBX.diagonal.texture);
  const texCenter   = useTexture(OBSTACLE_SHAPE_FBX.center.texture);
  const texSquare   = useTexture(OBSTACLE_SHAPE_FBX.square.texture);
  const texTriangle = useTexture(OBSTACLE_SHAPE_FBX.triangle.texture);

  const fbxByShape = useMemo<Record<ObstacleShape, THREE.Group>>(() => ({
    regular: fbxRegular, diagonal: fbxDiagonal, center: fbxCenter, square: fbxSquare, triangle: fbxTriangle,
  }), [fbxRegular, fbxDiagonal, fbxCenter, fbxSquare, fbxTriangle]);
  const texByShape = useMemo<Record<ObstacleShape, THREE.Texture>>(() => {
    const rec = { regular: texRegular, diagonal: texDiagonal, center: texCenter, square: texSquare, triangle: texTriangle };
    for (const t of Object.values(rec)) { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true; } }
    return rec;
  }, [texRegular, texDiagonal, texCenter, texSquare, texTriangle]);

  const geoByShape = useMemo(() => {
    const out: Partial<Record<ObstacleShape, THREE.BufferGeometry | null>> = {};
    for (const shape of WALL_SHAPES) out[shape] = getObstacleMergedGeometry(shape, fbxByShape[shape]);
    return out;
  }, [fbxByShape]);

  // Shared obstacle materials (normal + dim) per shape — cached module-side and
  // shared with any classic selected obstacle, so a settings edit rebuilds once.
  const matsByShape = useMemo(() => {
    const out: Partial<Record<ObstacleShape, ObstacleMats>> = {};
    for (const shape of WALL_SHAPES) out[shape] = getObstacleMaterials(gp, shape, texByShape[shape]);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gp.obstacleFillColor, gp.obstacleFillOpacity, gp.obstacleFresnelColor,
      gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity, gp.obstacleOutlineColor,
      gp.obstacleOutlineEmission, texByShape]);

  const meshRefs = useRef<Record<string, THREE.InstancedMesh | null>>({});
  const slotMaps = useRef<Record<string, Int32Array>>(null!);
  if (!slotMaps.current) {
    const m: Record<string, Int32Array> = {};
    for (const shape of WALL_SHAPES) for (const p of OBS_POOLS) m[`${shape}-${p}`] = new Int32Array(OBS_POOL_LIMIT);
    slotMaps.current = m;
  }
  // Per-obstacle grid-pop state, keyed by absolute slide index (edit/scrub only).
  const popState = useRef(new Map<number, ObsPopState>());
  const counts = useRef<Record<string, number>>({});

  useEffect(() => {
    for (const k of Object.keys(meshRefs.current)) {
      const m = meshRefs.current[k];
      if (m) m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoByShape, matsByShape]);

  // ── The writer: place + classify every visible obstacle each frame ──
  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const gridTol = Math.max(0.006, store.scrollStep * 0.55);
    const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
    const playing = store.playback.playing;
    const obstacleScale = gp.obstacleScale;
    const configs = gp.obstacleConfigs;
    const pop = popState.current;
    const c = counts.current;
    for (const shape of WALL_SHAPES) for (const p of OBS_POOLS) c[`${shape}-${p}`] = 0;

    let vis = 0;
    // Iterate FAR → NEAR (entries are sorted near→far by beat, so walk it in
    // reverse). A single InstancedMesh draws its instances in SLOT order, so we
    // have to bake the back-to-front transparent order into the slots ourselves —
    // otherwise overlapping see-through walls blend in the wrong order and their
    // outlines pile up / show through each other. (The classic per-mesh path got
    // this ordering for free from three's transparent sort.)
    for (let _ei = entries.length - 1; _ei >= 0; _ei--) {
      const { slide, index } = entries[_ei];
      const wt = slide.wall_type;
      if (!CONFIGURABLE_KEYS.has(wt)) continue;
      const cfg = configs[wt as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS[wt as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS.center;
      const shape = cfg.shape;
      const geo = geoByShape[shape];
      if (!geo) continue;

      const beat = slide.time_beats;
      const rel = beat - rawBeat;
      // Draw-distance (FOV) cull: only instances within the horizon are written.
      if (rel > PERF.obstacleViewBeats || rel < -behind) continue;

      // Grid-alignment pop (edit/scrub aid; off during playback). Same crossing +
      // onGrid trigger as the classic ticker, tracked per absolute index.
      const onGrid = Math.abs(rel) < gridTol;
      let ps = pop.get(index);
      if (!ps) { ps = { pulseStart: 0, prevOnGrid: false, prevRel: rel }; pop.set(index, ps); }
      const crossed = ps.prevRel > 0 && rel <= 0;
      const trig = onGrid || crossed;
      if (trig && !ps.prevOnGrid) ps.pulseStart = nowMs;
      ps.prevOnGrid = trig;
      ps.prevRel = rel;
      let pulse = 1;
      if (!playing && ps.pulseStart) {
        const dt = (nowMs - ps.pulseStart) / 1000;
        const PD = 0.18;
        if (dt < PD) pulse = 1 + 0.4 * Math.sin(Math.PI * (dt / PD));
        else ps.pulseStart = 0;
      }

      const dim = beat < rawBeat && !onGrid;
      const poolKey = `${shape}-${dim ? 'dim' : 'normal'}`;
      const mesh = meshRefs.current[poolKey];
      if (!mesh) continue;
      const slot = c[poolKey];
      if (slot >= OBS_POOL_LIMIT) continue;

      const [wx, wy] = resolveWallXY(wt, slide.position, cfg);
      const isLeft = wt === 'wall_left' || wt === 'angle_left';
      const bs = obstacleScale * pulse;
      _oPos.set(wx, NOTE_Y_OFFSET + wy, -beat * BEAT_SPACING);
      // Per-obstacle roll from the .synth (zRotation, e.g. 90°/270° — most VR
      // walls carry one) ON TOP of the wall-type's default rotation. Without it,
      // walls the game shows "lying down" render upright.
      _oEuler.set(0, 0, (cfg.rotation + (slide.z_rotation ?? 0)) * DEG2RAD);
      _oQuat.setFromEuler(_oEuler);
      _oScale.set(
        (isLeft ? -bs : bs) * (cfg.scaleX ?? 1),
        bs * (cfg.scaleY ?? 1),
        bs * (cfg.scaleZ ?? 1),
      );
      _oMatrix.compose(_oPos, _oQuat, _oScale);
      mesh.setMatrixAt(slot, _oMatrix);
      slotMaps.current[poolKey][slot] = index;
      c[poolKey] = slot + 1;
      vis++;
    }

    for (const shape of WALL_SHAPES) {
      for (const p of OBS_POOLS) {
        const m = meshRefs.current[`${shape}-${p}`];
        if (!m) continue;
        m.count = c[`${shape}-${p}`];
        m.instanceMatrix.needsUpdate = true;
      }
    }
    // Prune stale pop state so the map can't grow without bound over a session.
    if (pop.size > 1024) pop.clear();

    RENDER_STATS.obsVisible = vis + _classicObsVisible;
    RENDER_STATS.obsMounted = entries.length + _obstacleReg.size;
  });

  // ── Events (instanceId → absolute slide index), mirroring ObstacleModel ──
  const makeHandlers = (key: string) => ({
    onClick: (e: ThreeEvent<MouseEvent>) => {
      const id = (e as { instanceId?: number }).instanceId;
      if (id == null) return;
      const index = slotMaps.current[key]?.[id];
      if (index == null) return;
      const s = useEditorStore.getState();
      if (s.toolMode !== 'select') return;
      e.stopPropagation();
      const cur = s.selectedSlideIds;
      if (e.nativeEvent.shiftKey) {
        s.selectSlides(cur.includes(index) ? cur.filter(i => i !== index) : [...cur, index]);
      } else {
        s.selectNotes([]);
        s.selectSlides([index]);
      }
    },
    onContextMenu: (e: ThreeEvent<MouseEvent>) => {
      const id = (e as { instanceId?: number }).instanceId;
      if (id == null) return;
      const index = slotMaps.current[key]?.[id];
      if (index == null) return;
      e.stopPropagation();
      e.nativeEvent.preventDefault();
      useEditorStore.getState().removeSlide(index);
    },
  });

  return (
    <>
      {WALL_SHAPES.map((shape) => {
        const geo = geoByShape[shape];
        const mat = matsByShape[shape];
        if (!geo || !mat) return null;
        return (
          <React.Fragment key={shape}>
            <instancedMesh
              ref={(m) => { meshRefs.current[`${shape}-normal`] = m; }}
              args={[geo, mat.normal, OBS_POOL_LIMIT]}
              frustumCulled={false}
              {...makeHandlers(`${shape}-normal`)}
            />
            <instancedMesh
              ref={(m) => { meshRefs.current[`${shape}-dim`] = m; }}
              args={[geo, mat.dim, OBS_POOL_LIMIT]}
              frustumCulled={false}
              {...makeHandlers(`${shape}-dim`)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

// ── Instanced AIR obstacles (Square / Triangle) — VR ─────────────────────────
// Synth Riders VR maps carry Square and Triangle obstacles (the .synth `Squares`
// / `Triangles` lists) — elevated frames you dodge, distinct from the lane walls.
// They were only drawn in Console mode; this renders them in VR too, using the
// real VR obstacle shapes (SquareObstacle2 / TriangleObstacle2), stood upright
// and rolled by each obstacle's own zRotation. Free position (x,y from the
// obstacle, not a lane) + one instanced draw per shape.

const AIR_SHAPES: ObstacleShape[] = ['square', 'triangle'];

export function InstancedAirObstaclePools({ entries }: {
  entries: Array<{ ob: Obstacle; shape: ObstacleShape; index: number }>;
}) {
  const gp = useEditorStore(s => s.gameplaySettings);

  const fbxSquare   = useFBX(OBSTACLE_SHAPE_FBX.square.fbx);
  const fbxTriangle = useFBX(OBSTACLE_SHAPE_FBX.triangle.fbx);
  const texSquare   = useTexture(OBSTACLE_SHAPE_FBX.square.texture);
  const texTriangle = useTexture(OBSTACLE_SHAPE_FBX.triangle.texture);

  const texByShape = useMemo<Record<string, THREE.Texture>>(() => {
    const rec = { square: texSquare, triangle: texTriangle };
    for (const t of Object.values(rec)) { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true; } }
    return rec;
  }, [texSquare, texTriangle]);

  const geoByShape = useMemo(() => ({
    // Math.PI/2: stand the flat-authored Square/Triangle FBX upright.
    square: getObstacleMergedGeometry('square', fbxSquare, Math.PI / 2),
    triangle: getObstacleMergedGeometry('triangle', fbxTriangle, Math.PI / 2),
  } as Partial<Record<ObstacleShape, THREE.BufferGeometry | null>>), [fbxSquare, fbxTriangle]);

  const matsByShape = useMemo(() => ({
    square: getObstacleMaterials(gp, 'square', texByShape.square),
    triangle: getObstacleMaterials(gp, 'triangle', texByShape.triangle),
  } as Partial<Record<ObstacleShape, ObstacleMats>>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gp.obstacleFillColor, gp.obstacleFillOpacity, gp.obstacleFresnelColor,
     gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity, gp.obstacleOutlineColor,
     gp.obstacleOutlineEmission, texByShape]);

  const meshRefs = useRef<Record<string, THREE.InstancedMesh | null>>({});
  const counts = useRef<Record<string, number>>({});

  useEffect(() => {
    for (const k of Object.keys(meshRefs.current)) {
      const m = meshRefs.current[k];
      if (m) m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoByShape, matsByShape]);

  useFrame(() => {
    const rawBeat = chartBeat();
    const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
    const obstacleScale = gp.obstacleScale;
    const c = counts.current;
    for (const shape of AIR_SHAPES) c[shape] = 0;

    for (const { ob, shape, index } of entries) {
      const geo = geoByShape[shape];
      if (!geo) continue;
      const beat = ob.time_beats ?? 0;
      const rel = beat - rawBeat;
      if (rel > PERF.obstacleViewBeats || rel < -behind) continue;
      const mesh = meshRefs.current[shape];
      if (!mesh) continue;
      const slot = c[shape];
      if (slot >= OBS_POOL_LIMIT) continue;

      const px = ob.position ? ob.position[0] : 0;
      const py = ob.position ? ob.position[1] : 0;
      _oPos.set(px * GRID_SCALE * 10, py * GRID_SCALE * 10 + NOTE_Y_OFFSET, -beat * BEAT_SPACING);
      _oEuler.set(0, 0, (ob.z_rotation ?? 0) * DEG2RAD);
      _oQuat.setFromEuler(_oEuler);
      _oScale.set(obstacleScale, obstacleScale, obstacleScale);
      _oMatrix.compose(_oPos, _oQuat, _oScale);
      mesh.setMatrixAt(slot, _oMatrix);
      c[shape] = slot + 1;
      void index;
    }

    for (const shape of AIR_SHAPES) {
      const m = meshRefs.current[shape];
      if (!m) continue;
      m.count = c[shape];
      m.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <>
      {AIR_SHAPES.map((shape) => {
        const geo = geoByShape[shape];
        const mat = matsByShape[shape];
        if (!geo || !mat) return null;
        return (
          <instancedMesh
            key={shape}
            ref={(m) => { meshRefs.current[shape] = m; }}
            args={[geo, mat.normal, OBS_POOL_LIMIT]}
            frustumCulled={false}
          />
        );
      })}
    </>
  );
}

// ── Main ObstacleModel Component ─────────────────────────────────────────────

export default function ObstacleModel({
  slide,
  index,
  selected = false,
}: {
  slide: Slide;
  /** Index into the current difficulty's slides array (for right-click delete). */
  index?: number;
  /** True when this obstacle is box-/select-all'd (draws a highlight). */
  selected?: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null!);
  const configs = useEditorStore(s => s.gameplaySettings.obstacleConfigs);
  // Mesh refs collected by ObstacleFBX (stable array identity across renders).
  const meshesRef = useRef<THREE.Mesh[]>([]);
  // This obstacle's shape material pair, filled by ObstacleFBX (child renders
  // before this parent's effect runs, so it's set by registration time).
  const matsRef = useRef<ObstacleMats | null>(null);

  // Register with the central ticker (ONE loop updates all obstacles).
  useEffect(() => {
    const entry: ObstacleEntry = {
      beats: slide.time_beats,
      group: groupRef.current,
      meshes: meshesRef.current,
      matsRef,
      appliedMats: null,
      pulseStart: 0,
      prevOnGrid: false,
      dimmed: false,
    };
    _obstacleReg.add(entry);
    return () => { _obstacleReg.delete(entry); };
  }, [slide.time_beats]);

  // Right-click on an obstacle: delete it.
  const handleContextMenu = (e: import('@react-three/fiber').ThreeEvent<MouseEvent>) => {
    if (index == null) return;
    e.stopPropagation();
    e.nativeEvent.preventDefault();
    useEditorStore.getState().removeSlide(index);
  };

  // Left-click in Select mode: select this obstacle (Shift = add/toggle) so it
  // can be deleted together with notes. In other tool modes, don't hijack it.
  const handleClick = (e: import('@react-three/fiber').ThreeEvent<MouseEvent>) => {
    if (index == null) return;
    const s = useEditorStore.getState();
    if (s.toolMode !== 'select') return;
    e.stopPropagation();
    const cur = s.selectedSlideIds;
    if (e.nativeEvent.shiftKey) {
      s.selectSlides(cur.includes(index) ? cur.filter(i => i !== index) : [...cur, index]);
    } else {
      s.selectNotes([]);
      s.selectSlides([index]);
    }
  };

  const highlight = selected ? (
    <mesh renderOrder={999}>
      <boxGeometry args={[1.9, 2.7, 0.5]} />
      <meshBasicMaterial color="#FFD54A" wireframe transparent opacity={0.9} depthTest={false} fog={false} />
    </mesh>
  ) : null;

  const wt = slide.wall_type;

  // Get per-obstacle position from config, or fallback to 0,0
  const cfg = CONFIGURABLE_KEYS.has(wt)
    ? configs[wt as ObstacleConfigKey]
    : null;

  // Per-instance position takes over from the wall_type default once the wall
  // has been moved with the gizmo (X/Y); falls back to the config default when
  // absent OR implausible (see resolveWallXY — legacy-editor position noise).
  const [wallX, wallY] = resolveWallXY(wt, slide.position, cfg);
  const xPos = wallX;
  const yPos = NOTE_Y_OFFSET + wallY;

  // Per-obstacle roll from the .synth (zRotation) — matches the instanced pool so
  // a selected wall keeps its authored orientation. ObstacleFBX adds the wall
  // type's own cfg.rotation on top (both around Z, so they simply sum).
  const zRot = ((slide.z_rotation ?? 0) * Math.PI) / 180;

  // Check if we have an FBX model for this type
  const hasModel = CONFIGURABLE_KEYS.has(wt);
  if (!hasModel) {
    const wallColor = WALL_COLORS[wt] || '#ff444488';
    return (
      <group ref={groupRef} position={[xPos, yPos, 0]} rotation={[0, 0, zRot]} onContextMenu={handleContextMenu} onClick={handleClick}>
        <FallbackSlideMarker color={wallColor} />
        {highlight}
      </group>
    );
  }

  return (
    <group ref={groupRef} position={[xPos, yPos, 0]} rotation={[0, 0, zRot]} onContextMenu={handleContextMenu} onClick={handleClick}>
      <ObstacleFBX wallType={wt} meshesOut={meshesRef.current} matsRef={matsRef} />
      {highlight}
    </group>
  );
}
